import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api, { timeAgo } from "@/lib/api";
import { CoinIcon, Pct, fmtPrice } from "@/components/layernav/primitives";
import { COLORS } from "@/components/home/LayerIndexCard";
import { AuroraGauge } from "@/components/home/AuroraArc";
import { sectorTrack, fmtBig } from "./SectorHero";

const CARD = "rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 sm:p-5";
const HEAD = "font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-[#A78BFA]";

const TopMovers = () => {
  const { data } = useQuery({
    queryKey: ["markets"],
    queryFn: async () => (await api.get("/markets")).data,
    refetchInterval: 90000,
  });
  const movers = [...(data?.coins || [])]
    .sort((a: any, b: any) => Math.abs(b.change_24h ?? 0) - Math.abs(a.change_24h ?? 0))
    .slice(0, 5);
  return (
    <div className={CARD} data-testid="mi-top-movers">
      <p className={HEAD}>Top Movers · 24H</p>
      {movers.length ? (
        <ul className="mt-3 space-y-0.5">
          {movers.map((c: any, i: number) => (
            <li key={c.symbol}>
              <a href={`/coin/${c.symbol}`} data-testid={`mover-${c.symbol}`}
                onClick={() => sectorTrack("market_module_interaction", `mover_${c.symbol}`)}
                className="grid grid-cols-[18px_minmax(0,1fr)_86px_60px] items-center gap-2 rounded-lg px-2 py-2 -mx-2 hover:bg-white/[0.05] transition-colors">
                <span className="font-mono text-[10px] text-gray-600">{i + 1}</span>
                <span className="flex items-center gap-2.5 min-w-0">
                  <CoinIcon symbol={c.symbol} size={22} />
                  <span className="font-mono text-[12.5px] font-bold text-gray-200">{c.symbol}</span>
                </span>
                <span className="font-mono text-[13px] text-white text-right">{fmtPrice(c.price)}</span>
                <Pct v={c.change_24h} className="!text-[11.5px] text-right" />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="font-mono text-[11px] text-gray-600 py-4">Market data temporarily unavailable</p>
      )}
    </div>
  );
};

const RANGES = [
  { key: "1D", hours: 24 },
  { key: "7D", hours: 168 },
  { key: "30D", hours: 720 },
  { key: "90D", hours: 2160 },
];

const MarketOverview = () => {
  const [range, setRange] = useState("1D");
  const { data: hist } = useQuery({
    queryKey: ["mcap-history"],
    queryFn: async () => (await api.get("/markets/global/history?hours=2400")).data,
    staleTime: 10 * 60 * 1000,
  });
  const { data: g } = useQuery({
    queryKey: ["markets-global"],
    queryFn: async () => (await api.get("/markets/global")).data,
    staleTime: 5 * 60 * 1000,
  });
  const points: { t: string; mcap: number }[] = hist?.points || [];
  const spanHours = useMemo(() => {
    if (points.length < 2) return 0;
    return (new Date(points[points.length - 1].t).getTime() - new Date(points[0].t).getTime()) / 36e5;
  }, [points]);
  // Only expose ranges the real observation history can support (≥80% coverage)
  const available = RANGES.filter((r) => spanHours >= r.hours * 0.8 || r.key === "1D");
  const active = available.some((r) => r.key === range) ? range : "1D";
  const hours = RANGES.find((r) => r.key === active)!.hours;
  const cut = Date.now() - hours * 36e5;
  const series = points.filter((p) => new Date(p.t).getTime() >= cut)
    .map((p) => ({ t: p.t, mcap: p.mcap }));
  const up = series.length > 1 && series[series.length - 1].mcap >= series[0].mcap;
  const stroke = up ? "#34D399" : "#F0564E";
  return (
    <div className={CARD} data-testid="mi-market-overview">
      <div className="flex items-center justify-between gap-2">
        <p className={HEAD}>Market Overview</p>
        <div className="flex gap-1" role="tablist" aria-label="Chart range">
          {available.map((r) => (
            <button key={r.key} role="tab" aria-selected={active === r.key}
              data-testid={`overview-range-${r.key}`}
              onClick={() => { setRange(r.key); sectorTrack("market_range_change", r.key); }}
              className={`rounded px-2 py-0.5 font-mono text-[10px] font-bold transition-colors ${
                active === r.key ? "bg-[#7C3AED]/25 text-white" : "text-gray-500 hover:text-gray-300"}`}>
              {r.key}
            </button>
          ))}
        </div>
      </div>
      <div className="h-[132px] mt-2 -mx-1">
        {series.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 6, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="mcapFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="t" hide />
              <YAxis domain={["dataMin", "dataMax"]} hide />
              <Tooltip content={({ active: a, payload }) => a && payload?.length ? (
                <div className="rounded-lg border border-white/10 bg-black/85 px-3 py-2 font-mono text-[11px] text-white">
                  {fmtBig(payload[0].value as number)}
                  <span className="block text-gray-500 text-[9px] mt-0.5">{timeAgo(payload[0].payload.t)}</span>
                </div>
              ) : null} />
              <Area type="monotone" dataKey="mcap" stroke={stroke} strokeWidth={2}
                fill="url(#mcapFill)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <p className="font-mono text-[11px] text-gray-600 pt-10 text-center">Collecting market history…</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 pt-3 border-t border-white/[0.07]">
        {[["Market Cap", fmtBig(g?.total_market_cap_usd)],
          ["24H Volume", fmtBig(g?.volume_24h_usd)],
          ["BTC Dominance", g?.btc_dominance != null ? `${Number(g.btc_dominance).toFixed(1)}%` : "—"],
          ["ETH Dominance", g?.eth_dominance != null ? `${Number(g.eth_dominance).toFixed(1)}%` : "—"]]
          .map(([l, v]) => (
            <div key={l as string} className="flex items-baseline justify-between gap-2">
              <span className="font-mono text-[9px] uppercase tracking-wider text-gray-500">{l}</span>
              <span className="font-mono text-[12.5px] font-bold text-white">{v}</span>
            </div>
          ))}
      </div>
    </div>
  );
};

const MarketSentiment = () => {
  const { data } = useQuery({
    queryKey: ["layer-index"],
    queryFn: async () => (await api.get("/layer-index")).data,
    staleTime: 5 * 60 * 1000,
  });
  const value: number | null = data?.value ?? null;
  const history: { value: number; label?: string }[] = data?.history || [];
  const prev = history.length > 1 ? history[history.length - 2] : null;
  const delta = value != null && prev ? value - prev.value : null;
  const color = data ? COLORS[data.label] || "#A3A3A3" : "#A3A3A3";
  return (
    <div className={CARD} data-testid="mi-market-sentiment">
      <p className={HEAD}>Market Sentiment</p>
      <div className="w-[150px] mx-auto mt-1 group">
        <AuroraGauge pct={value ?? 0} num={value ?? "—"} label={data?.label} labelColor={color} delta={delta}
          done animating={false} glint={false} dormant={value == null} compact />
      </div>
      <div className="flex items-center justify-center gap-4 font-mono text-[10.5px] mt-1">
        <span className="text-gray-400">Now <span className="font-bold text-white">{value ?? "—"}</span></span>
        {prev && <span className="text-gray-500">Yesterday <span className="text-gray-300">{prev.value}</span></span>}
      </div>
      <p className="font-mono text-[9px] uppercase tracking-wider text-gray-600 text-center mt-2.5">
        NewsLayer Layer Index · editorial + market signals
      </p>
    </div>
  );
};

export const MarketIntelligence = () => (
  <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10" data-testid="market-intelligence">
    <div className="flex items-baseline justify-between gap-3 mb-4">
      <h2 className="font-heading font-bold text-white text-lg">Market Intelligence</h2>
      <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Live data</span>
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      <TopMovers />
      <MarketOverview />
      <MarketSentiment />
    </div>
  </section>
);
